/*
 * NOTE: This component is currently NOT IN USE as of April 2026.
 * 
 * Due to temporary VAT reduction on food in Sweden (in effect until December 31, 2027),
 * the delivery option has been moved earlier in the customer flow,
 * since this selection can affect product pricing.
 * 
 * This component allowed selection AFTER products were added 
 * to cart, making it incompatible with the new requirements.
 * 
 * After December 31, 2027, we need to evaluate whether to reintroduce this  
 * delivery options step in the customer flow, or remove it.
 * 
 */

import * as React from 'react'
import { connect } from 'react-redux'
import type { RootState, Dispatch } from '../../../app/store'
import Page from '../../layout/Page'
import loader from '../../../images/loading.gif'
import styles from './DeliveryOptions.module.css'
import { useNavigate, useParams } from 'react-router-dom'
import Modal from '../../Modal'
import { StoreT } from '../../../models/store'
import DeliveryItem from '../../DeliveryItem'
import { getDiscountErrorMessage } from '../../../utils/discountHelper'
import { DeliveryProviderT, DeliveryProvidersT, isAvailable } from '../../../utils/deliveryHelper'

export type CouponValidation = {
  valid?: boolean,
  reasons?: {
    reasonCode: string
  }[]
}

interface CheckoutT {
  cartId: string | null,
  token: string | null,
  paymentStatus: number,
  getPaymentStatus: (payload: { storeId: string }) => Promise<void>,
  setPaymentStatus: any,
  createOrder: (payload?: any) => Promise<void>,
  currentOrderId: string | null
  paymentUrl: any,
  fetchCart: () => Promise<void>,
  fetchActiveDeliveryProviders: (storeId: string) => Promise<DeliveryProvidersT>,
  location: any
  currentStore: StoreT | null
  addItemToCart: (payload: { storeId: string, productId: string, itemType: number }) => void,
  validateCoupon: ((payload: string) => Promise<CouponValidation>)
  removeCoupon: (payload: string) => Promise<void>
  couponApplied: boolean
  tableNumber: string | undefined
  emptyCart: boolean
}

// Payment status
// Unknown = 0,
// Initialized = 100,
// Completed = 200,
// Failed = 300,
// Cancelled = 400


function DeliveryOptions ({ tableNumber, fetchActiveDeliveryProviders, createOrder, currentOrderId, addItemToCart, validateCoupon, removeCoupon, couponApplied, cartId, emptyCart }: CheckoutT) {
  const { storeId } = useParams()
  const navigate = useNavigate()

  const [wantToApplyDiscount, setWantToApplyDiscount] = React.useState(false)
  const [discountCode, setDiscountCode] = React.useState('')
  const [discountErrorMessage, setDiscountErrorMessage] = React.useState<string>('')
  const [selectedOption, setSelectedOption] = React.useState<DeliveryProviderT | null>(null)
  const [deliveryProviders, setDeliveryProviders] = React.useState<DeliveryProvidersT | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [loadingDiscount, setLoadingDiscount] = React.useState(false)

  const tableDeliveryTypeId = 'deliverId'

  React.useEffect(() => {
    async function getDeliveryOptions (storeId: string) {
      const providers: DeliveryProvidersT = await fetchActiveDeliveryProviders(storeId)
      setDeliveryProviders(providers.filter(provider => provider.availabilityHours.some(day => isAvailable(day))))
    }

    storeId && getDeliveryOptions(storeId)
  }, [])


  const handleSubmit = React.useCallback(async (event) => {
    event.preventDefault()
    setDiscountErrorMessage('')
    createOrder()
  }, [discountCode, selectedOption])

  React.useEffect(() => {
    if (currentOrderId) {
      navigate(`/butik/${storeId}/betala/${currentOrderId}`)
    }
  }, [currentOrderId])



  const handleSelect = React.useCallback(async (item) => {
    setLoading(true)
    try {
      if (storeId) {
        setSelectedOption(item)
        await addItemToCart({ storeId, productId: item.id, itemType: item.deliveryProviderType })
      }
    } finally {
      setLoading(false)
    }
  }, [storeId, addItemToCart])


  const handleModalSubmit = async () => {
    setLoadingDiscount(true)
    setDiscountErrorMessage('')
    if (discountCode.length > 0) {
      try {
        const { valid, reasons } = await validateCoupon(discountCode)
        if (valid) {
          setWantToApplyDiscount(false)
        } else {
          setDiscountErrorMessage(getDiscountErrorMessage(reasons && reasons?.length > 0 ? reasons[0].reasonCode : ''))
        }
      } finally {
        setLoadingDiscount(false)
      }

    } else {
      setDiscountErrorMessage('Vänligen ange en rabattkod')
    }
  }

  return (
    <Page withCancelButton={true} withCart={true} withBackButton={true}>
      <section className={styles.checkout}>
        <div className={styles.content}>
          <h1 className={styles.heading}>Leveransalternativ</h1>
          <form id='orderDetails' onSubmit={handleSubmit} className={styles.orderDetails}>
            <fieldset className={styles.deliveryOptionsContainer}>
              {tableNumber ?
                <DeliveryItem
                  id={tableDeliveryTypeId}
                  active={selectedOption?.id === tableDeliveryTypeId}
                  displayName={`Bord: ${tableNumber}`}
                  messages={['Bordsservering']}
                  loading={selectedOption?.id === tableDeliveryTypeId && loading}
                  onSelect={() => handleSelect({ id: tableDeliveryTypeId, deliveryProviderType: 600 })}
                  price={0}
                /> :
                !deliveryProviders ?
                  <div>
                    <img src={loader} alt="Loading..." />
                  </div>
                  : deliveryProviders.length > 0 ? deliveryProviders.map((option) => {
                    const messages: string[] = []
                    option.deliveryTime && messages.push(option.deliveryTime)
                    option.description && messages.push(option.description)

                    return (
                      <DeliveryItem
                        loading={selectedOption?.id === option.id && loading}
                        key={option.id}
                        id={option.id}
                        active={selectedOption?.id === option.id}
                        displayName={option.name}
                        messages={messages}
                        onSelect={() => handleSelect(option)}
                        price={option.price} />
                    )
                  }) :
                    <>
                      <DeliveryItem
                        id={'EATIN'}
                        active={selectedOption?.deliveryProviderType === 600}
                        displayName={`Äta här`}
                        loading={selectedOption?.deliveryProviderType === 600 && loading}
                        onSelect={() => handleSelect({ id: tableDeliveryTypeId, deliveryProviderType: 600 })}
                        price={0}
                      />
                      <DeliveryItem
                        id={'TAKEAWAY'}
                        active={selectedOption?.deliveryProviderType === 700}
                        displayName={`Ta med`}
                        loading={selectedOption?.deliveryProviderType === 600 && loading}
                        onSelect={() => handleSelect({ id: tableDeliveryTypeId, deliveryProviderType: 700 })}
                        price={0}
                      />
                    </>
              }
            </fieldset>
          </form>
          <div className={styles.buttonsContainer}>
            <button className={styles.lightButton} type="button" onClick={() => !couponApplied ? setWantToApplyDiscount(prev => !prev) : cartId && removeCoupon(cartId)}>
              {!couponApplied ? 'Lägg till rabattkod' : 'Ta bort rabattkod'}
              <></>
            </button>
            <button disabled={!selectedOption || emptyCart} className={styles[!selectedOption || emptyCart ? 'inactiveButton' : 'mainButton']} type="submit" form='orderDetails'>
              <>Fortsätt</>
            </button>
          </div>
        </div>
      </section>
      <Modal appearFrom='bottom' active={wantToApplyDiscount} maxWidth="600px">
        <div className={styles.modalContent}>
          <>
            {discountErrorMessage && <p className={styles.errorMessage}>{discountErrorMessage}</p>}
            <label>Skriv din rabattkod</label>
            <input className={discountErrorMessage ? styles.errorValueBox : couponApplied ? styles.validValueBox : styles.valueBox} value={discountCode} onChange={(e) => {
              setDiscountErrorMessage('')
              setDiscountCode(e.target.value)
            }} placeholder='Ange rabattkod'></input>
          </>
          {/* } */}
          <div className={styles.modalButtonContainer}>
            <button onClick={handleModalSubmit} className={styles.mainButton} type="button" form='orderDetails'>
              {loadingDiscount ?
                'Laddar...'
                : 'Registrera'}
            </button>
            <button onClick={() => setWantToApplyDiscount(false)} className={styles.lightButton} type="button" form='orderDetails'>
              Stäng
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  )
}

function mapState (state: RootState) {
  return {
    token: state.order.swishToken,
    cartId: state.cart.cartId,
    paymentStatus: state.order.paymentStatus,
    currentOrderId: state.order.currentOrderId,
    paymentUrl: state.order.paymentUrl,
    currentStore: state.store.current,
    couponApplied: state.cart.couponApplied,
    tableNumber: state.cart.tableNumber,
    emptyCart: state.cart.items.length === 0
  }
}
function mapDispatch (dispatch: Dispatch) {
  return {
    getPaymentStatus: dispatch.order.getPaymentStatus,
    createOrder: dispatch.order.createOrder,
    setPaymentStatus: dispatch.order.setPaymentStatus,
    fetchCart: dispatch.cart.fetchCart,
    fetchActiveDeliveryProviders: dispatch.store.fetchActiveDeliveryProviders,
    addItemToCart: dispatch.cart.addItemToCart,
    validateCoupon: dispatch.cart.validateCoupon,
    removeCoupon: dispatch.cart.removeCoupon,
  };
}

export default connect(mapState, mapDispatch)(DeliveryOptions)